import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output, TemplateRef,
    ViewChild,
} from '@angular/core';
import { MatMenu, MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import {
    ActionEventArgs,
    CellClickEventArgs,
    DayService,
    DragAndDropService,
    DragEventArgs,
    EventClickArgs,
    EventRenderedArgs,
    EventSettingsModel,
    PopupOpenEventArgs,
    RenderCellEventArgs,
    ResizeEventArgs,
    ResizeService,
    ScheduleModule,
    WeekService,
} from '@syncfusion/ej2-angular-schedule';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, reduce, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';
import { loadCldr } from '@syncfusion/ej2-base';
import * as n1 from 'cldr-data/main/de/currencies.json';
import * as n2 from 'cldr-data/main/de/timeZoneNames.json';
import * as n3 from 'cldr-data/main/de/numbers.json';
import * as n4 from 'cldr-data/main/de/ca-gregorian.json';
import * as s from 'cldr-data/supplemental/currencyData.json';
import * as s2 from 'cldr-data/supplemental/numberingSystems.json';
import { MatCardModule } from '@angular/material/card';
import { DaveTasksModule } from '../../../dave-tasks/dave-tasks.module';
import { AppFilterModule } from '../../../dave-utils-module/app-filter-module/app-filter.module';
import { EventStateEnum, ITaskPriority, taskEventStates } from '../../../dave-data-module/entities/event.entity';
import { ITaskBoardSort, TaskBoardFilter } from '../../../dave-tasks/components/tasks-board/tasks-board.component';
import { FilterTypes } from '../../../services/default-filter.service';
import { clacEventFilterAmount, getEventFilterValues, IEventFilterType } from '../../../helper/event.helper';
import {
    FILTER_TYPE_BOOLEAN,
    FilterOption,
} from '../../../dave-utils-module/app-filter-module/app-filter/app-filter.component';
import { TaskFilterService } from '../../../dave-tasks/services/task-filter.service';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatBadgeModule } from '@angular/material/badge';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatButtonModule } from '@angular/material/button';
import { AppButtonModule } from '../../../dave-utils-module/app-button-module/app-button.module';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DaveDoubleIconModule } from '../../../dave-double-icon/dave-double-icon.module';
import {
    DetailTasksComponent,
    DetailTasksComponentDialogData,
} from '../../../dave-event-card/components/detail-tasks/detail-tasks.component';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { UserEntity } from '../../../dave-data-module/entities/user.entity';
import { getUser, getUsersActive } from '../../../dave-data-module/State/selectors/users.selectors';
import { State } from '../../../dave-data-module/State';
import { getEventById, getTasks, getTasksFetched } from '../../../dave-data-module/State/selectors/events.selectors';
import { MatCheckboxModule } from '@angular/material/checkbox';
import moment from 'moment/moment';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { DaveEventCardModule } from '../../../dave-event-card/dave-event-card.module';
import { FrontendDate } from '../../../dave-data-module/helper/backend-frontend-conversion.helper';
import { EventsActionTypes } from '../../../dave-data-module/State/actions/events.actions';
import { TaskPageMeta } from '../../../helper/page-metadata';
import { getBookmarkByEventId } from '../../../dave-data-module/State/selectors/bookmarks.selectors';
import { CompactEnumType } from '../../../dave-event-card/components/event-card/event-card.component';
import {
    SelectFolderDialogComponent,
    SelectFolderDialogComponentDialogData,
} from '../../../dave-file-explorer/components/select-folder-dialog/select-folder-dialog.component';
import { ofType } from '@ngrx/effects';
import { FilesActionTypes } from '../../../dave-data-module/State/actions/files.actions';
import {
    DaveFilePreviewComponent,
    DaveFilePreviewComponentDialogData,
} from '../../../dave-file-preview-dialog/components/dave-file-preview/dave-file-preview.component';

/**
 * Ein im Wochenplan farblich unterscheidbarer Bearbeiter.
 * index 0 = aktueller Nutzer ("eigene Aufgaben"), index 1-3 = zusätzlich hinzugefilterte Bearbeiter.
 */
export interface WeekPlanTrackedUser {
    UserId: number;
    DisplayName: string;
    Color: string;
}


export interface WeekPlanTaskInput {
    Id: number;
    Subject: string;
    ProcessingStartDate: Date | null;
    ProcessingEndDate: Date | null;
    UserIds: number[];
    IsCompleted?: boolean;
    isReadonly?: boolean;
}


interface WeekPlanEventData {
    Id: number;
    Subject: string;
    StartTime: Date;
    EndTime: Date;
    IsAllDay: boolean;
    UserIds: number[];
    IsCompleted: boolean;
    isReadonly: boolean;
    meta: WeekPlanTaskInput;
}

export const WEEK_PLAN_MAX_ADDITIONAL_USERS = 3;

/** index 0 = eigene Aufgaben, index 1-3 = zusätzlich gefilterte Bearbeiter */
export const WEEK_PLAN_DEFAULT_COLORS: string[] = [
    '#00897b', // eigene Aufgaben
    '#ff9800', // Bearbeiter 1
    '#4caf50', // Bearbeiter 2
    '#9c27b0', // Bearbeiter 3
];

export interface WeekPlanCellClickEvent {
    Start: Date;
    End: Date;
}

export interface WeekPlanRescheduleEvent {
    Id: number;
    ScheduledStartDate: Date;
    ScheduledEndDate: Date;
}

export function buildWeekPlanBackground(colors: string[]): string | null {
    if (!colors.length) {
        return null;
    }
    if (colors.length === 1) {
        return colors[0];
    }
    const stripeSize = 14;
    const stops: string[] = [];
    colors.forEach((color, i) => {
        stops.push(`${color} ${i * stripeSize}px`, `${color} ${(i + 1) * stripeSize}px`);
    });
    return `repeating-linear-gradient(45deg, ${stops.join(', ')})`;
}

@Component({
    selector: 'app-week-plan',
    standalone: true,
    imports: [CommonModule, FormsModule, ScheduleModule, MatMenuModule, MatCardModule, MatFormFieldModule, MatInputModule, MatBadgeModule, FontAwesomeModule, DaveTasksModule, AppFilterModule, MatButtonModule, ReactiveFormsModule, AppButtonModule, MatTooltipModule, DaveDoubleIconModule, MatCheckboxModule, DragDropModule, DaveEventCardModule],
    providers: [DayService, WeekService, ResizeService, DragAndDropService],
    templateUrl: './week-plan.component.html',
    styleUrls: ['./week-plan.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WeekPlanComponent implements OnInit, OnDestroy {
    @ViewChild('scheduleObj') schedule;
    @ViewChild('eventTemplate', { static: true }) eventTemplate!: TemplateRef<WeekPlanEventData>;


    protected SelectedDate: Date = new Date();

    private normalizeRange(start: Date, end: Date): { start: Date; end: Date; isAllDay: boolean } {
        const isMidnight = start.getHours() === 0 && start.getMinutes() === 0 && start.getSeconds() === 0;
        if (isMidnight && start.getTime() === end.getTime()) {
            const nextDay = new Date(start);
            nextDay.setDate(nextDay.getDate() + 1);
            return { start: new Date(start), end: nextDay, isAllDay: true };
        }
        return { start, end, isAllDay: false };
    }

    //Rename Button on the top right
    public onActionComplete(args: ActionEventArgs): void {
        if (args.requestType === "viewNavigate" || args.requestType === "toolBarItemRendered") {
            let text = "Aktuelle Woche";
            let todayElement = this.schedule.element.querySelector(".e-schedule .e-schedule-toolbar .e-today .e-tbar-btn-text");
            todayElement.innerHTML = text;
        }
    }


    private ownUserId$: Observable<number | null> = this.store.select(getUser).pipe(
        map((user) => user?.Id ?? null),
        shareReplay({ bufferSize: 1, refCount: true }),
    );
    private userDisplayNames$ = new BehaviorSubject<Record<number, string>>({});
    private tasks$ = new BehaviorSubject<WeekPlanTaskInput[]>([]);
    private onDestroy$ = new Subject<void>();

    readonly CalendarDropListId = 'weekPlanCalendarDropList';



    public readonly defaultHiddenStates = [
        EventStateEnum.OnHold,
        EventStateEnum.Done
    ];


    public selectedBoardColumns: EventStateEnum[] = Object.values(taskEventStates)
        .filter(state => !this.defaultHiddenStates.includes(state));


    public SearchBarActive = false;
    public SearchForm = new FormControl<string>('');
    @ViewChild('input') private inputRef?: ElementRef<HTMLInputElement>;

    SearchButtonClicked() {
        if (!this.SearchBarActive) {
            this.SearchBarActive = true;
            setTimeout(() => this.inputRef?.nativeElement.focus(), 30);
        } else {
            if (this.SearchForm.value.length === 0) {
                this.SearchBarActive = false;
            }
        }
    }


    public TaskBoardFilterValues$: BehaviorSubject<IEventFilterType> = getEventFilterValues();
    protected taskBoardFilter$: Observable<TaskBoardFilter> = this.TaskBoardFilterValues$.pipe(
        map((v) => ({
            CommissionIds: v[FilterTypes.CommissionId]?.map((u) => +u.id) || null,
            UserId: v[FilterTypes.UserId]?.map((u) => +u.id) || null,
            AutorId: v[FilterTypes.AutorId]?.map((u) => +u.id) || null,
            Priority: v.priority?.length ? v.priority.map((p) => +p.id as ITaskPriority) : null,
            MinEventEndDate: v[FilterTypes.EndDate]?.from ? v[FilterTypes.EndDate].from.toDate() : null,
            MaxEventEndDate: v[FilterTypes.EndDate]?.to ? v[FilterTypes.EndDate].to.toDate() : null,
            MilestoneIds: v[FilterTypes.Milestones]?.map((u) => +u.id) || null,
            ShowReleased:  v[FilterTypes.ShowReleased],
            ShowOnHold:  v[FilterTypes.ShowOnHold],
            ShowCompleted:  v[FilterTypes.ShowCompleted],
        })),
    );

    onPopupOpen(args: PopupOpenEventArgs): void {
        args.cancel = true;
    }

    onCellClick(args: CellClickEventArgs): void {
        args.cancel = true;
        const cell: WeekPlanCellClickEvent = { Start: args.startTime, End: args.endTime };
        this.OpenAddTaskDialog(cell);
    }

    OpenAddTaskDialog(cell?: WeekPlanCellClickEvent): void {
        const selectedUserIds = this.selectedUserIds$.value;
        const start = cell?.Start ? moment(cell.Start) : null;
        const end = cell?.End ? moment(cell.End) : null;
        let wholeDay = false;
        if (
            start &&
            end &&
            start.hour() === 0 &&
            start.minute() === 0 &&
            end.hour() === 0 &&
            end.minute() === 0
        ) {
            wholeDay = true;
        }

        this.dialog.open<DetailTasksComponent, DetailTasksComponentDialogData>(DetailTasksComponent, {
            ...DetailTasksComponent.DefaultConfig,
            data: {
                EventId: null,
                InitialValues: {
                    CommissionId:
                        this.TaskBoardFilterValues$.value?.[FilterTypes.CommissionId]?.length === 1
                            ? +this.TaskBoardFilterValues$.value[FilterTypes.CommissionId][0].id
                            : null,
                    UserIds: selectedUserIds.length ? selectedUserIds : null,
                    ProcessingStartDate: cell?.Start ? moment(cell.Start) : null,
                    ProcessingStartTime: cell?.Start ? moment(cell.Start).format('HH:mm') : null,
                    ProcessingEndDate: end ? moment(end) : null,
                    ProcessingEndTime: end ? moment(end).format('HH:mm') : null,
                    WholeDay: wholeDay,
                },
            },
        });
    }

    async onCalendarDrop(event: CdkDragDrop<unknown, unknown, number>) {
        const point = event.dropPoint ?? { x: (event.event as MouseEvent)?.clientX, y: (event.event as MouseEvent)?.clientY };

        if (point == null) {
            return;
        }
        const targetElement = document.elementFromPoint(point.x, point.y) as HTMLElement | null;
        const workCell = targetElement?.closest('.e-work-cells') as HTMLElement | null;
        const allDayCell = targetElement?.closest('.e-all-day-cells') as HTMLElement | null;

        const dropCell = workCell ?? allDayCell;
        if (!dropCell) {
            return;
        }
        const cellDetails = this.schedule.getCellDetails(dropCell);
        const eventEntity = await firstValueFrom(this.store.select(getEventById({id: event.item.data})));
        const userIds = [...new Set([
            ...eventEntity.UserIds,
            ...this.selectedUserIds$.value,
        ])];

        this.rescheduleTask(event.item.data, cellDetails.startTime, cellDetails.endTime, userIds);
    }

    public ShowCompleted$ = this.TaskBoardFilterValues$.pipe(map((v) => !!v[FilterTypes.ShowCompleted]));
    public ShowReleased$ = this.TaskBoardFilterValues$.pipe(map((v) => !!v[FilterTypes.ShowReleased]));
    public ShowOnHold$ = this.TaskBoardFilterValues$.pipe(map((v) => !!v[FilterTypes.ShowOnHold]));


    public TaskBoardSort: ITaskBoardSort = { sortBy: 'endDate', direction: 'dsc' };
    OpenFile(fileId: number) {
        this.dialog.open<DaveFilePreviewComponent, DaveFilePreviewComponentDialogData>(DaveFilePreviewComponent, {
            ...DaveFilePreviewComponent.DefaultConfig,
            data: {
                fileId,
            },
        });

    }
    public TaskBoardFilterSettings$: Observable<FilterOption[]> = this.filterService
        .filterSettingsWithMilestones$(
            this.TaskBoardFilterValues$.pipe(
                map((f) => (f[FilterTypes.CommissionId]?.length === 1 ? +f[FilterTypes.CommissionId][0].id : null)),
                distinctUntilChanged(),
            ),
        )
        .pipe(
            map((fs) => [
                ...fs,
                {
                    Name: FilterTypes.ShowReleased,
                    Label: 'Rückmeldung anzeigen',
                    Type: FILTER_TYPE_BOOLEAN,
                },
                {
                    Name: FilterTypes.ShowOnHold,
                    Label: 'On hold anzeigen',
                    Type: FILTER_TYPE_BOOLEAN,
                },
                {
                    Name: FilterTypes.ShowCompleted,
                    Label: 'Erledigt anzeigen',
                    Type: FILTER_TYPE_BOOLEAN,
                },
            ]),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
    public TaskBoardFilterAmount$ = this.TaskBoardFilterValues$.pipe(map(clacEventFilterAmount), shareReplay({ refCount: true, bufferSize: 1 }));

    private trackedUsers: WeekPlanTrackedUser[] = [];

    constructor(
        private filterService: TaskFilterService,
        private dialog: MatDialog,
        private store: Store<State>,
    ) {
        loadCldr(n1, n2, n3, n4, s, s2);
    }

    protected set SelectedUserIds(ids: number[] | null) {
        this.selectedUserIds$.next((ids ?? []).slice(0, WEEK_PLAN_DEFAULT_COLORS.length));
    }
    protected SelectedUserIdsChange = new EventEmitter<number[]>();

    private selectedUserIds$ = new BehaviorSubject<number[]>([]); // leer = initial nichts ausgewählt, auch nicht der eigene Nutzer

    public AllUsers$: Observable<UserEntity[]> = this.store.select(getUsersActive);

    public TrackedUsers$: Observable<WeekPlanTrackedUser[]> = combineLatest([this.ownUserId$, this.selectedUserIds$, this.userDisplayNames$]).pipe(
        map(([ownUserId, selectedUserIds, names]) => {
            // Ist der eigene Nutzer mit ausgewählt, bekommt er fix Farbe[0]; alle anderen in Auswahlreihenfolge.
            const otherIds = selectedUserIds.filter((id) => id !== ownUserId);
            const orderedIds = ownUserId != null && selectedUserIds.includes(ownUserId) ? [ownUserId, ...otherIds] : otherIds;
            return orderedIds.slice(0, WEEK_PLAN_DEFAULT_COLORS.length).map<WeekPlanTrackedUser>((id, i) => ({
                UserId: id,
                DisplayName: names[id] ?? `Nutzer ${id}`,
                Color: WEEK_PLAN_DEFAULT_COLORS[i],
            }));
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public UserSelectionContext$: Observable<{ SelectedUserIds: number[]; ColorByUserId: Record<number, string> }> = combineLatest([this.selectedUserIds$, this.TrackedUsers$]).pipe(
        map(([selectedUserIds, trackedUsers]) => ({
            SelectedUserIds: selectedUserIds,
            ColorByUserId: Object.fromEntries(trackedUsers.map((tu) => [tu.UserId, tu.Color])),
        })),
        shareReplay({ bufferSize: 1, refCount: true }),
    );



    public EventSettings$: Observable<EventSettingsModel> = combineLatest([
        this.tasks$,
        this.TrackedUsers$
    ]).pipe(
        map(([tasks, trackedUsers]) => {
            const trackedUserIds = trackedUsers.map((u) => u.UserId);

            const dataSource: WeekPlanEventData[] = tasks
                .filter((t) => !!t.ProcessingStartDate && !!t.ProcessingEndDate)
                .filter((t) => t.UserIds?.some((id) => trackedUserIds.includes(id)))
                .map((t) => {
                    const range = this.normalizeRange(t.ProcessingStartDate as Date, t.ProcessingEndDate as Date);
                    return {
                        Id: t.Id,
                        Subject: t.Subject,
                        StartTime: range.start,
                        EndTime: range.end,
                        IsAllDay: range.isAllDay,
                        UserIds: t.UserIds,
                        IsCompleted: !!t.IsCompleted,
                        isReadonly: !!t.isReadonly,
                        meta: t,
                    };
                });
            // Syncfusion types the Angular TemplateRef as Function although the wrapper
            // resolves the referenced ng-template at runtime.
            return { dataSource, template: this.eventTemplate as unknown as Function };
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    isUserSelected(userId: number, ctx: { SelectedUserIds: number[] }): boolean {
        return ctx.SelectedUserIds.includes(userId);
    }

    isUserSelectionDisabled(userId: number, ctx: { SelectedUserIds: number[] }): boolean {
        return !ctx.SelectedUserIds.includes(userId) && ctx.SelectedUserIds.length >= WEEK_PLAN_DEFAULT_COLORS.length;
    }

    toggleUserSelection(userId: number): void {
        const current = this.selectedUserIds$.value;
        if (current.includes(userId)) {
            const next = current.filter((id) => id !== userId);
            this.selectedUserIds$.next(next);
            this.SelectedUserIdsChange.emit(next);
            return;
        }
        if (current.length >= WEEK_PLAN_DEFAULT_COLORS.length) {
            return; // max. 4 gleichzeitig, UI ist dafür bereits per disabled abgesichert
        }
        const next = [...current, userId];
        this.selectedUserIds$.next(next);
        this.SelectedUserIdsChange.emit(next);
    }




    ngOnInit(): void {
        this.TrackedUsers$.pipe(takeUntil(this.onDestroy$)).subscribe((tu) => (this.trackedUsers = tu));

        this.store.select(getTasksFetched).pipe(
            filter(Boolean),
            switchMap(() => this.store.select(getTasks)),
            takeUntil(this.onDestroy$),
        ).subscribe((tasks) => {
            this.tasks$.next(tasks.map((task) => ({
                Id: task.Id,
                Subject: task.Name || '',
                ProcessingStartDate: task.ProcessingStartDate,
                ProcessingEndDate: task.ProcessingEndDate,
                UserIds: task.UserIds || [],
                IsCompleted: task.State === EventStateEnum.Done,
            })));
        });

        this.ownUserId$
            .pipe(
                filter((id): id is number => id != null),
                take(1),
                takeUntil(this.onDestroy$),
            )
            .subscribe((id) => {
                if (this.selectedUserIds$.value.length === 0) {
                    this.selectedUserIds$.next([id]);
                    this.SelectedUserIdsChange.emit([id]);
                }
            });
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    // ---- Farb-/Streifen-Logik -------------------------------------------------

    /**
     * Färbt bzw. streift die Kachel je nach zugewiesenen (und aktuell sichtbaren) Bearbeitern.
     * 1 sichtbarer Bearbeiter -> Vollfarbe. Mehrere -> diagonale Streifen
     */
    onEventRendered(args: EventRenderedArgs): void {
        const data = args.data as WeekPlanEventData;
        if (!args.element) {
            return;
        }

        const colors = this.trackedUsers.filter((tu) => data.UserIds?.includes(tu.UserId)).map((tu) => tu.Color);
        const background = buildWeekPlanBackground(colors);
        if (!background) { return; }
        args.element.style.background = background;
        args.element.style.color = this.getContrastColor(colors[0]);
        args.element.classList.toggle('week-plan-completed', data.IsCompleted);
        if (data.isReadonly) {
            args.element.classList.add('week-plan-readonly');
        }
    }

    protected getContrastColor(hex: string): string {
        const clean = hex.replace('#', '');
        const r = parseInt(clean.substring(0, 2), 16);
        const g = parseInt(clean.substring(2, 4), 16);
        const b = parseInt(clean.substring(4, 6), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.6 ? '#000000' : '#ffffff';
    }


    // ---- Verschieben / Größe ändern --------------------------------------------

    IsDraggingCalendarEvent = false;

    onDragStart(args: DragEventArgs): void {
        const data = args.data as unknown as WeekPlanEventData;
        this.IsDraggingCalendarEvent = true;
        if (data.isReadonly) {
            args.cancel = true;
            return;
        }

        const element = args.element as HTMLElement;
        const rect = element.getBoundingClientRect();

        document.documentElement.style.setProperty(
            '--week-plan-drag-width',
            `${rect.width}px`
        );

        document.documentElement.style.setProperty(
            '--week-plan-drag-height',
            `${rect.height}px`
        );
    }

    onDragStop(args: DragEventArgs): void {
        this.IsDraggingCalendarEvent = false;
        const data = args.data as unknown as WeekPlanEventData;
        const dropTarget = (args.event as any)?.target as HTMLElement | undefined;
        const droppedOnTaskBoard = !!dropTarget?.closest('.tasks-board-content');
        if (droppedOnTaskBoard) {
            args.cancel = true;
            this.removeTaskFromPlan(data.Id);
            return;
        }

        const droppedOnAllDay = !!dropTarget?.closest('.e-all-day-cells') || !!(args.data as any)?.IsAllDay;
        if (droppedOnAllDay) {
            let durationDays = 1;
            if (data.meta?.ProcessingStartDate && data.meta?.ProcessingEndDate) {
                const existingStart = new Date(data.meta.ProcessingStartDate);
                existingStart.setHours(0, 0, 0, 0);
                const existingEnd = new Date(data.meta.ProcessingEndDate);
                existingEnd.setHours(0, 0, 0, 0);
                const diffMs = existingEnd.getTime() - existingStart.getTime();
                const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
                if (diffDays > 1) {
                    durationDays = diffDays;
                }
            }

            const referenceDate = data.StartTime ?? data.EndTime;
            const start = new Date(referenceDate);
            start.setHours(0, 0, 0, 0);

            const end = new Date(start);
            end.setDate(end.getDate() + durationDays);
            end.setHours(0, 0, 0, 0);

            this.rescheduleTask(data.Id, start, end, null);
            return;
        }

        this.rescheduleIfChanged(data);
    }


    onResizeStart(args: ResizeEventArgs): void {
        const data = args.data as unknown as WeekPlanEventData;
        if (data.isReadonly) {
            args.cancel = true;
        }
    }

    onResizeStop(args: ResizeEventArgs): void {
        this.rescheduleIfChanged(args.data as unknown as WeekPlanEventData);
    }

    private readonly compactThresholdMinutes = 90;

    private readonly compactBiggerThresholdMinutes = 60;

    isCompactEvent(data: WeekPlanEventData): CompactEnumType {
        const durationMinutes = (data.EndTime.getTime() - data.StartTime.getTime()) / 60000;
        return durationMinutes < this.compactBiggerThresholdMinutes ? CompactEnumType.Compact : durationMinutes < this.compactThresholdMinutes || durationMinutes >= 1440 ? CompactEnumType.BiggerCompact : CompactEnumType.Normal;
    }




    private  rescheduleTask(id: number, start: Date, end: Date, userIds?: number[]): void {
        this.store.dispatch(
            EventsActionTypes.ModifyEvent({
                Payload: {
                    id,
                    processingStartDate: FrontendDate(start),
                    processingEndDate: FrontendDate(end),
                    userIds: userIds ? userIds : null,
                },
            }),
        );
    }

    private removeTaskFromPlan(id: number): void {
        this.store.dispatch(
            EventsActionTypes.ModifyEvent({
                Payload: {
                    id,
                    processingStartDate: null,
                    processingEndDate: null,
                },
            }),
        );
    }

    private rescheduleIfChanged(data: WeekPlanEventData): void {
        if (!data?.meta) {
            return;
        }
        const startChanged = data.StartTime.getTime() !== data.meta.ProcessingStartDate?.getTime();
        const endChanged = data.EndTime.getTime() !== data.meta.ProcessingEndDate?.getTime();
        if (startChanged || endChanged) {
            this.rescheduleTask(data.Id, data.StartTime, data.EndTime, null);
        }
    }
    public DragPreviewBackground$: Observable<string | null> = this.TrackedUsers$.pipe(
        map((users) => buildWeekPlanBackground(users.map((u) => u.Color))),
        shareReplay({ bufferSize: 1, refCount: true }),
    );
}
